import { DateAdapter } from '@angular/material/core';

export interface DateTimeHourParts {
   hours: string;
   dayPeriod: string;
}

/**
 * Adapts type `D` to be usable as a datetime by cdk-based components that work with datetimes.
 * Compatible with the material DateAdapter.
 */
export abstract class LcsDateTimeAdapter<D> extends DateAdapter<D> {
   /**
    * Creates a date with the given year, month, and date. Does not allow over/under-flow of the
    * month and date.
    * @param year The full year of the date time. (e.g. 89 means the year 89, not the year 1989).
    * @param month The month of the date time (0-indexed, 0 = January). Must be an integer 0 - 11.
    * @param date The day of month of the date. Must be an integer 1 - length of the given month.
    * @param hours The hour of the date time. Must be an integer 1 - length of the given month.
    * @param minutes The minutes of the date time. Must be an integer 1 - 60.
    * @param seconds The seconds of the date time. Must be an integer 1 - 60.
    * @returns The new date, or null if invalid.
    */
   abstract createDateTime(
      year: number,
      month: number,
      date: number,
      hours: number,
      minutes: number,
      seconds: number
   ): D;

   abstract getHours(date: D): number;

   abstract getMinutes(date: D): number;

   abstract getSeconds(date: D): number;

   abstract addHours(date: D, hours: number): D;

   abstract addMinutes(date: D, minutes: number): D;

   abstract getDayStart(date: D): D;

   abstract getDayEnd(date: D): D;

   abstract getDayStartUtc(date: D): D;

   abstract getDayEndUtc(date: D): D;

   abstract getOpposingPeriod(date: D, displayFormat: any): string;

   abstract formatHours(date: D, displayFormat: any): DateTimeHourParts;

   abstract formatMinutes(date: D, displayFormat: object): string;

   abstract formatDateTime(date: D, displayFormat: object): string;
}

export abstract class LcsRelativeDateTimeAdapter<D, TRelativeDate> extends LcsDateTimeAdapter<D | TRelativeDate> {
   abstract isRelativeDate(value: any): value is TRelativeDate;
   /**
    * Serializes a date without its time component
    */
   abstract serializeDateOnly(value: D | TRelativeDate): string | null;
}
